<script>
import { mapState } from "vuex";
import MessageBar from "./MessageBar.vue";
import Navigation from "./navigation/Navigation.vue";
import Breadcrumbs from "./navigation/Breadcrumbs";
import Header from "./header/Header.vue";
import NotificationPoller from "./common/NotificationPoller.vue";
import UploadManager from "./upload-v2/UploadManager.vue";

export default {
  components: {
    Breadcrumbs,
    MessageBar,
    Navigation,
    Header,
    NotificationPoller,
    UploadManager
  },
  computed: {
    ...mapState({
      isLoading: (state) => (state.loader.length > 0),
      maintenanceEnabled: (state) => state.maintenanceMode.enabled,
      maintenanceScheduledAt: (state) => state.maintenanceMode.scheduledAt,
      maintenanceInitialized: (state) => state.maintenanceMode.initialized,
      systemBannerEnabled: (state) => state.systemBanner.enabled,
      systemBannerLevel: (state) => state.systemBanner.level,
      systemBannerMessage: (state) => state.systemBanner.message,
      isAdmin: (state) => state.auth.userData?.isAdmin ?? false,
      isAuthenticated: (state) => !!state.auth.userData,
    }),
    isAuthenticationRoute() {
      return this.$route?.path?.startsWith("/oauth/redirect") || this.$route?.name === "logged-out";
    },
    hasScheduledMaintenance() {
      return !!this.maintenanceScheduledAt;
    },
    formattedScheduledMaintenance() {
      if (!this.maintenanceScheduledAt) {
        return "";
      }
      return new Date(this.maintenanceScheduledAt).toLocaleString();
    },
    showSystemBanner() {
      return this.systemBannerEnabled && !!(this.systemBannerMessage || "").trim();
    },
    systemBannerClass() {
      return `system-banner--${this.systemBannerLevel || "info"}`;
    },
  },
  created() {
    this.$store.dispatch("initPage");
  },
};
</script>

<template>
  <div v-if="!isAuthenticated && isAuthenticationRoute" class="authentication-route">
    <router-view/>
    <MessageBar />
  </div>
  <div v-else-if="!isAuthenticated || !maintenanceInitialized" class="maintenance-loading">
    <m-linear-progress
        :buffer="0.2"
        :open="true"
        :progress="0.2"
        indeterminate
    />
    <MessageBar />
  </div>
  <div v-else-if="maintenanceEnabled && !isAdmin" class="maintenance-container">
    <div v-if="showSystemBanner" class="system-banner" :class="systemBannerClass">
      <span class="material-icons">campaign</span>
      <span>{{ systemBannerMessage }}</span>
    </div>
    <div class="maintenance-banner">
      <span class="material-icons">build</span>
      <span>{{ $t('maintenance.banner.down') }}</span>
    </div>
    <main class="maintenance-main">
      <article>
        <router-view/>
      </article>
      <MessageBar />
    </main>
  </div>
  <div v-else class="app-wrapper">
    <div v-if="showSystemBanner" class="system-banner" :class="systemBannerClass">
      <span class="material-icons">campaign</span>
      <span>{{ systemBannerMessage }}</span>
    </div>
    <div v-if="maintenanceEnabled || hasScheduledMaintenance" class="maintenance-banner inline">
      <span class="material-icons">build</span>
      <span v-if="maintenanceEnabled">{{ $t('maintenance.banner.down') }}</span>
      <span v-else>{{ $t('maintenance.banner.scheduled', { time: formattedScheduledMaintenance }) }}</span>
    </div>
    <div class="container">
      <Header class="header" />
      <Navigation class="navigation" />
      <div class="top-bar">
        <Breadcrumbs />
        <portal-target class="actions-portal" name="actions" multiple/>
      </div>
      <main>
        <m-linear-progress
            :buffer="0.2"
            :open="isLoading"
            :progress="0.2"
            class="progress"
            indeterminate
        />
        <article>
          <router-view/>
        </article>
        <MessageBar />
        <NotificationPoller v-if="isAuthenticated" />
        <UploadManager v-if="isAuthenticated" />
      </main>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.container {
  display: grid;
  grid-template-columns: minmax(16rem, 16rem) minmax(0, 1fr);
  grid-template-rows: minmax(auto, auto) minmax(auto, auto) minmax(0, 1fr);
  grid-template-areas:
    'header header'
    'navigation top'
    'navigation content';
  height: 100%;
}

.app-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.app-wrapper > .container {
  flex: 1 1 auto;
}

.header {
  grid-area: header;
}

.navigation {
  grid-area: navigation;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.top-bar {
  grid-area: top;

  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #E0E0E0;
  padding: 0 2rem;
}

.actions-portal {
  align-items: center;
  display: flex;
  justify-content: flex-end;
  padding-right: 1.5rem;
  white-space: nowrap;
}

.actions-portal > ::v-deep .storage-job-action-menu,
.actions-portal > ::v-deep .admin-action-menu {
  margin-left: 0.35rem;
}

.actions-portal > ::v-deep button {
  align-items: center;
  background: transparent;
  border: 1px solid #1275CC;
  color: #1275CC;
  border-radius: 4px;
  display: inline-flex;
  margin-left: 1rem;
  padding: 0.75rem 1rem;
  font-weight: bold;
  cursor: pointer;
  line-height: 1.6rem;

  &:disabled {
    cursor: default;
    opacity: 0.2;
  }

  &.primary {
    background: #1275CC;
    border-color: transparent;
    color: white;
  }

  &.icon-action {
    height: 2.25rem;
    justify-content: center;
    line-height: 1;
    padding: 0;
    width: 2.25rem;

    > i {
      padding-right: 0;
      vertical-align: 0;
    }
  }

  > i {
    vertical-align: -0.35rem;
    padding-right: 0.25rem;
  }
}

@media (max-width: 600px) {
  .actions-portal > ::v-deep button {
    display: none;
  }
}

main {
  grid-area: content;

  overflow: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

.progress {
  position: sticky;
  height: 4px;
  margin-bottom: -4px;
}

article {
  padding: 0.5rem 2rem 5rem;
}

.maintenance-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.authentication-route {
  min-height: 100%;
}

.maintenance-banner {
  background: #c62828;
  color: white;
  padding: 1rem;
  font-weight: bold;
  letter-spacing: 0.05rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
}

.maintenance-banner .material-icons {
  font-size: 1.8rem;
}

.maintenance-banner.inline {
  justify-content: center;
}

.system-banner {
  color: white;
  padding: 0.85rem 1rem;
  font-weight: bold;
  letter-spacing: 0.03rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
}

.system-banner--info {
  background: #1976d2;
}

.system-banner--warning {
  background: #ff8f00;
}

.system-banner--alert {
  background: #c62828;
}

.system-banner--green {
  background: #2e7d32;
}

.maintenance-main {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
}

.maintenance-main article {
  max-width: 34rem;
  width: 100%;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.maintenance-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
}
</style>

<style lang="scss">
@import './css-reset.scss';
@import './material.scss';

body {
  background: #F5F6F8;
  line-height: 1.25;
}

body, input, button {
  font: 1rem Arial, sans-serif;
}
</style>
